import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiClock,
  FiCheckSquare,
  FiPower,
  FiImage,
  FiCopy,
  FiArrowRight,
  FiAlertTriangle,
  FiGlobe,
} from "react-icons/fi";
import {
  fetchTaskList,
  createTask,
  editTask,
  deleteTask,
  setTaskActive,
  fetchTaskCompletions,
  fetchTaskDetails,
  fetchTaskEvents,
} from "../../utils/taskApi";
import TaskModal from "./components/TaskModal";
import TaskHistoryModal from "./components/TaskHistoryModal";
import TimeCell from "./components/TimeCell";
import PoolTab from "./components/PoolTab";
import ScheduleTab from "./components/ScheduleTab";
import PinTaskModal from "./components/PinTaskModal";
import CopyToEventModal from "./components/CopyToEventModal";
import ConfirmActionModal from "../events/components/ConfirmActionModal";
import { useStickyState } from "../../hooks/useStickyState";
import { COUNTRIES, countryName } from "../../utils/countries";
import {
  TASK_TYPE_LABEL,
  TASK_TYPE_BADGE,
  TASK_TYPE_TEXT,
  PLATFORM_LABEL,
  formatDuration,
} from "./taskConstants";

// Tasks is the catalogue, Pool is the same rows read as *stock*, and Schedule
// is what the generator has actually published from that stock. They are three
// views of one pipeline, in the order an admin walks it.
// Pool and Schedule drive the daily scheduler, which is switched off — tasks
// belong to an event now. The screens are kept in the codebase rather than
// deleted, but showing tabs for machinery that no longer feeds the app would
// be inviting an admin to configure something with no effect.
const MAIN_TABS = [
  { key: "tasks", label: "Tasks" },
  { key: "completions", label: "Completions" },
];

/**
 * Where a task is shown. Global is the usual answer, so it reads quietly; a
 * targeted task shows its codes, and the full names on hover.
 */
const CountryCell = ({ countries }) => {
  const list = Array.isArray(countries) ? countries : [];
  if (list.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-400" title="Shown in every country">
        <FiGlobe size={11} /> Global
      </span>
    );
  }

  const shown = list.slice(0, 3);
  return (
    <span className="inline-flex flex-wrap items-center gap-1" title={list.map(countryName).join(", ")}>
      {shown.map((code) => (
        <span
          key={code}
          className="font-mono text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded"
        >
          {code}
        </span>
      ))}
      {list.length > shown.length && (
        <span className="text-[10px] font-semibold text-gray-500">+{list.length - shown.length}</span>
      )}
    </span>
  );
};

const TasksList = () => {
  const [mainTab, setMainTab] = useState("tasks");

  // ── Tasks tab ────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [search, setSearch] = useStickyState("", "tasks_search");
  const [debouncedSearch, setDebouncedSearch] = useStickyState("", "tasks_debouncedSearch");
  const [typeFilter, setTypeFilter] = useStickyState("", "tasks_typeFilter");
  const [platformFilter, setPlatformFilter] = useStickyState("", "tasks_platformFilter");
  const [statusFilter, setStatusFilter] = useStickyState("", "tasks_statusFilter");
  // "" = all, "global" = no targeting, or a code = tasks targeted at it.
  const [countryFilter, setCountryFilter] = useStickyState("", "tasks_countryFilter");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const searchTimer = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [historyTask, setHistoryTask] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, loading: false });

  // Pinning from a pool row, and a counter the Pool tab watches so that saving
  // a task on another tab refreshes the runway rather than leaving a stale
  // number on screen.
  const [pinTask, setPinTask] = useState(null);
  const [poolReloadKey, setPoolReloadKey] = useState(0);

  // A task belongs to an event, so the list needs to say which — and needs a
  // way to put a task into another one.
  const [eventFilter, setEventFilter] = useStickyState("", "tasks_eventFilter");
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  const runningEvent = events.find((event) => event.status === "running") || null;
  const unassigned = tasks.filter((task) => !task.event_id);

  useEffect(() => {
    fetchTaskEvents()
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      // The filter is a convenience; failing to load it must not break the page.
      .catch(() => setEvents([]));
  }, []);

  const loadTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const res = await fetchTaskList({
        page,
        limit: 20,
        search: debouncedSearch,
        taskType: typeFilter,
        platform: platformFilter,
        isActive: statusFilter,
        eventId: eventFilter,
        country: countryFilter,
      });
      setTasks(res.data || []);
      // Anything selected that this page no longer shows would be copied
      // invisibly — drop it rather than act on rows nobody can see.
      const visible = new Set((res.data || []).map((row) => row.id));
      setSelectedIds((prev) => prev.filter((id) => visible.has(id)));
      setLastPage(res.pagination?.total_pages || 1);
      setTotal(res.pagination?.total || 0);
    } catch (err) {
      toast.error(err.message || "Failed to load tasks");
    } finally {
      setLoadingTasks(false);
    }
  }, [page, debouncedSearch, typeFilter, platformFilter, statusFilter, eventFilter, countryFilter]);

  useEffect(() => {
    if (mainTab === "tasks") loadTasks();
  }, [mainTab, loadTasks]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(val);
    }, 400);
  };

  const openCreateModal = () => {
    setEditingTask(null);
    setModalOpen(true);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  /**
   * Edit a task the Pool tab handed over.
   *
   * A pool row is a *trimmed* task — it carries the scheduling fields but not
   * the description, links or video url. Opening the modal on one directly
   * would show those as empty and then save the emptiness over the real
   * values, so the full row is fetched first.
   */
  const openEditModalById = async (task) => {
    try {
      const res = await fetchTaskDetails(task.id);
      setEditingTask(res.data);
      setModalOpen(true);
    } catch (err) {
      toast.error(err.message || "Failed to load the task");
    }
  };

  const handleSaveTask = async (payload, id, isMultipart) => {
    if (id) {
      const res = await editTask(id, payload, isMultipart);
      toast.success(res.message || "Task updated successfully");
    } else {
      const res = await createTask(payload, isMultipart);
      toast.success(res.message || "Task created successfully");
    }
    loadTasks();
    setPoolReloadKey((k) => k + 1);
  };

  const handleToggleActive = async (task) => {
    try {
      const res = await setTaskActive(task.id, !task.is_active);
      toast.success(res.message || "Task status updated");
      loadTasks();
    } catch (err) {
      toast.error(err.message || "Failed to update task status");
    }
  };

  const handleDeleteClick = (task) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Task",
      message: `Are you sure you want to delete "${task.title}"?`,
      subMessage: "This removes the task and its image. Past completion records for it are kept.",
      confirmText: "Delete Task",
      confirmColor: "red",
      icon: FiTrash2,
      loading: false,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await deleteTask(task.id);
          toast.success(res.message || "Task deleted successfully");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadTasks();
        } catch (err) {
          toast.error(err.message || "Failed to delete task");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // ── Completions tab ─────────────────────────────────────────────────
  const [completions, setCompletions] = useState([]);
  const [loadingCompletions, setLoadingCompletions] = useState(false);
  const [taskIdFilter, setTaskIdFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [compPage, setCompPage] = useState(1);
  const [compLastPage, setCompLastPage] = useState(1);
  const [compTotal, setCompTotal] = useState(0);

  const loadCompletions = useCallback(async () => {
    setLoadingCompletions(true);
    try {
      const res = await fetchTaskCompletions({
        page: compPage,
        limit: 20,
        taskId: taskIdFilter.trim(),
        userId: userIdFilter.trim(),
      });
      setCompletions(res.data || []);
      setCompLastPage(res.pagination?.total_pages || 1);
      setCompTotal(res.pagination?.total || 0);
    } catch (err) {
      toast.error(err.message || "Failed to load completions");
    } finally {
      setLoadingCompletions(false);
    }
  }, [compPage, taskIdFilter, userIdFilter]);

  useEffect(() => {
    if (mainTab === "completions") loadCompletions();
  }, [mainTab, loadCompletions]);

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <FiCheckSquare size={20} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Task Management</h1>
                  <p className="text-xs text-gray-400 mt-1">
                    Tasks belong to an event. Users see every task in the running event and can finish them
                    whenever they like — completing one credits event points.
                    {runningEvent ? (
                      <>
                        {" "}
                        Live now: <span className="font-semibold text-gray-600">{runningEvent.name}</span>.
                      </>
                    ) : (
                      <> No event is running, so the app's Tasks screen is empty.</>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {(mainTab === "tasks" || mainTab === "completions") && (
                  <button
                    onClick={mainTab === "tasks" ? loadTasks : loadCompletions}
                    disabled={loadingTasks || loadingCompletions}
                    className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl transition shadow-sm"
                    title="Refresh"
                  >
                    <FiRefreshCw size={15} className={loadingTasks || loadingCompletions ? "animate-spin text-blue-600" : ""} />
                  </button>
                )}
                {mainTab === "tasks" && (
                  <button
                    onClick={openCreateModal}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-2"
                  >
                    <FiPlus size={16} /> Add Task
                  </button>
                )}
              </div>
            </div>

            {/* Main tabs */}
            <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit shadow-sm">
              {MAIN_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setMainTab(tab.key)}
                  className={`px-5 py-2 text-sm font-medium rounded-lg transition ${
                    mainTab === tab.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── Tasks tab ─────────────────────────────────────────────── */}
            {mainTab === "tasks" && (
              <>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                    <input
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search tasks..."
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                  </div>
                  <select
                    value={typeFilter}
                    onChange={(e) => {
                      setTypeFilter(e.target.value);
                      setPage(1);
                    }}
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">All Types</option>
                    {Object.entries(TASK_TYPE_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={platformFilter}
                    onChange={(e) => {
                      setPlatformFilter(e.target.value);
                      setPage(1);
                    }}
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">All Platforms</option>
                    {Object.entries(PLATFORM_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Country filter"
                    value={countryFilter}
                    onChange={(e) => {
                      setCountryFilter(e.target.value);
                      setPage(1);
                    }}
                    title="A country shows the tasks targeted at it — global tasks are listed under Global"
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">All Countries</option>
                    <option value="global">Global (every country)</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                  <select
                    value={eventFilter}
                    onChange={(e) => {
                      setEventFilter(e.target.value);
                      setPage(1);
                    }}
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">All Events</option>
                    <option value="none">No event (hidden from the app)</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {unassigned.length > 0 && selectedIds.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900">
                        {unassigned.length} task{unassigned.length === 1 ? "" : "s"} on this page belong to no
                        event.
                      </p>
                      <p className="text-amber-700 text-xs mt-0.5">
                        A task only appears in the app while its event is running, so these are invisible to
                        users. Pick an event for them.
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedIds(unassigned.map((task) => task.id))}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg transition shrink-0"
                    >
                      Select them
                    </button>
                  </div>
                )}

                {selectedIds.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-medium text-blue-900">
                      {selectedIds.length} task{selectedIds.length === 1 ? "" : "s"} selected
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedIds([])}
                        className="px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 rounded-lg transition"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setMoveOpen(true)}
                        title="Change which event these tasks belong to. Nothing is duplicated."
                        className="px-4 py-2 bg-white border border-blue-300 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition flex items-center gap-2"
                      >
                        <FiArrowRight size={14} /> Move to event
                      </button>
                      <button
                        onClick={() => setCopyOpen(true)}
                        title="Leave these where they are and create a second copy in another event."
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition flex items-center gap-2"
                      >
                        <FiCopy size={14} /> Copy to event
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          <th className="px-5 py-3.5 w-10">
                            <input
                              type="checkbox"
                              aria-label="Select all tasks on this page"
                              checked={tasks.length > 0 && selectedIds.length === tasks.length}
                              onChange={(e) =>
                                setSelectedIds(e.target.checked ? tasks.map((row) => row.id) : [])
                              }
                              className="w-4 h-4 accent-blue-600"
                            />
                          </th>
                          <th className="px-5 py-3.5">Task</th>
                          <th className="px-5 py-3.5">Event</th>
                          <th className="px-5 py-3.5">Countries</th>
                          <th className="px-5 py-3.5 text-right">Reward</th>
                          <th className="px-5 py-3.5 text-right">Started</th>
                          <th className="px-5 py-3.5 text-right">Done</th>
                          <th className="px-5 py-3.5">Status</th>
                          <th className="px-5 py-3.5 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {loadingTasks && tasks.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="px-5 py-12 text-center text-gray-400">
                              <div className="flex flex-col items-center gap-2">
                                <FiRefreshCw className="animate-spin text-blue-600" size={20} />
                                <span>Loading tasks...</span>
                              </div>
                            </td>
                          </tr>
                        ) : tasks.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="px-5 py-12 text-center text-gray-400">
                              <div className="flex flex-col items-center gap-2">
                                <FiCheckSquare size={28} className="text-gray-300" />
                                <p className="font-medium text-gray-500">No tasks found</p>
                                <p className="text-xs text-gray-400">Click "+ Add Task" to create one.</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          tasks.map((task) => (
                            <tr
                              key={task.id}
                              onClick={() => openEditModal(task)}
                              className="hover:bg-blue-50/40 transition cursor-pointer"
                              title="Open this task"
                            >
                              <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${task.title}`}
                                  checked={selectedIds.includes(task.id)}
                                  onChange={(e) =>
                                    setSelectedIds((prev) =>
                                      e.target.checked
                                        ? [...prev, task.id]
                                        : prev.filter((id) => id !== task.id)
                                    )
                                  }
                                  className="w-4 h-4 accent-blue-600"
                                />
                              </td>
                              {/* Identity in one cell: the picture, the name,
                                  and what kind of task it is. The description
                                  is deliberately not here — it is filled in
                                  from the task type, so it is the same
                                  sentence on nearly every row and reads as
                                  noise rather than information. */}
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-3 min-w-0">
                                  {task.image ? (
                                    <img
                                      src={task.image}
                                      alt=""
                                      className="w-9 h-9 rounded-lg object-cover border border-gray-100 shrink-0"
                                    />
                                  ) : (
                                    <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-300 shrink-0">
                                      <FiImage size={15} />
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 truncate">{task.title}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                      <span className={`font-medium ${TASK_TYPE_TEXT}`}>
                                        {TASK_TYPE_LABEL[task.task_type] || task.task_type}
                                      </span>
                                      {task.platform !== "both" && (
                                        <> · {PLATFORM_LABEL[task.platform] || task.platform} only</>
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3.5">
                                {task.event_name ? (
                                  <span className="text-xs text-gray-700">{task.event_name}</span>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"
                                    title="A task with no event never appears in the app"
                                  >
                                    <FiAlertTriangle size={11} /> No event
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3.5">
                                <CountryCell countries={task.countries} />
                              </td>
                              {/* Points and the time the link must stay open
                                  are one thought: what the task costs and
                                  pays. */}
                              <td className="px-5 py-3.5 text-right tabular-nums">
                                <span className="font-semibold text-gray-800">{task.reward_points}</span>
                                <span className="text-gray-400"> pts</span>
                                {task.visit_duration ? (
                                  <span className="block text-[11px] text-gray-400">{task.visit_duration}s visit</span>
                                ) : null}
                              </td>
                              {/* Everyone who opened the task, finished or not —
                                  so Done can never exceed it, and the gap
                                  between the two is the drop-off. */}
                              <td className="px-5 py-3.5 text-right tabular-nums">
                                <span className={task.started_count ? "font-semibold text-gray-800" : "text-gray-300"}>
                                  {task.started_count ?? 0}
                                </span>
                                {task.started_count > task.completed_count ? (
                                  <span className="block text-[11px] text-gray-400">
                                    {task.started_count - task.completed_count} unfinished
                                  </span>
                                ) : null}
                              </td>
                              <td className="px-5 py-3.5 text-right tabular-nums">
                                <span className={task.completed_count ? "font-semibold text-gray-800" : "text-gray-300"}>
                                  {task.completed_count}
                                </span>
                              </td>
                              {/* Active is the normal state, so it is quiet.
                                  Inactive is the exception worth spotting from
                                  across the table, so it is the loud one. */}
                              <td className="px-5 py-3.5">
                                {task.is_active ? (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-300">
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => openEditModal(task)}
                                    title="Edit task"
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                                  >
                                    <FiEdit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(task)}
                                    title="Delete task"
                                    className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 hover:text-rose-700 transition"
                                  >
                                    <FiTrash2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleActive(task)}
                                    title={task.is_active ? "Deactivate task" : "Activate task"}
                                    className={`p-1.5 rounded-lg transition ${
                                      task.is_active
                                        ? "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                        : "text-emerald-600 hover:bg-emerald-50"
                                    }`}
                                  >
                                    <FiPower size={16} />
                                  </button>
                                  <button
                                    onClick={() => setHistoryTask(task)}
                                    title="View completion history"
                                    className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                  >
                                    <FiClock size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {lastPage > 1 && (
                    <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Showing {tasks.length} of {total} tasks
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={page <= 1 || loadingTasks}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Previous
                        </button>
                        <span className="px-2 font-medium">
                          Page {page} of {lastPage}
                        </span>
                        <button
                          disabled={page >= lastPage || loadingTasks}
                          onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Pool tab ─────────────────────────────────────────────── */}
            {mainTab === "pool" && (
              <PoolTab
                reloadKey={poolReloadKey}
                onEditTask={openEditModalById}
                onPinTask={(task) => setPinTask(task)}
              />
            )}

            {/* ── Schedule tab ─────────────────────────────────────────── */}
            {mainTab === "schedule" && <ScheduleTab onChanged={() => setPoolReloadKey((k) => k + 1)} />}

            {/* ── Completions tab ───────────────────────────────────────── */}
            {mainTab === "completions" && (
              <>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center gap-3">
                  <input
                    value={taskIdFilter}
                    onChange={(e) => {
                      setTaskIdFilter(e.target.value);
                      setCompPage(1);
                    }}
                    placeholder="Filter by Task ID"
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  <input
                    value={userIdFilter}
                    onChange={(e) => {
                      setUserIdFilter(e.target.value);
                      setCompPage(1);
                    }}
                    placeholder="Filter by User ID"
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          <th className="px-5 py-3.5">User</th>
                          <th className="px-5 py-3.5">Task</th>
                          <th className="px-5 py-3.5">Type</th>
                          <th className="px-5 py-3.5">Status</th>
                          <th className="px-5 py-3.5">Reward Points</th>
                          <th className="px-5 py-3.5">Started At</th>
                          <th className="px-5 py-3.5">Completed At</th>
                          <th className="px-5 py-3.5" title="From opening the task to completing it">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {loadingCompletions && completions.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="px-5 py-12 text-center text-gray-400">
                              <div className="flex flex-col items-center gap-2">
                                <FiRefreshCw className="animate-spin text-blue-600" size={20} />
                                <span>Loading completions...</span>
                              </div>
                            </td>
                          </tr>
                        ) : completions.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="px-5 py-12 text-center text-gray-400">
                              No completions found.
                            </td>
                          </tr>
                        ) : (
                          completions.map((row) => (
                            <tr key={row.id} className="hover:bg-gray-50/70 transition">
                              <td className="px-5 py-3.5">
                                <p className="font-medium text-gray-800">{row.username || "—"}</p>
                                <p className="text-[11px] text-gray-400">{row.user_email}</p>
                              </td>
                              <td className="px-5 py-3.5 text-gray-700">{row.task_title}</td>
                              <td className="px-5 py-3.5">
                                <span className={`inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${TASK_TYPE_BADGE}`}>
                                  {TASK_TYPE_LABEL[row.task_type] || row.task_type}
                                </span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span
                                  className={`inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border capitalize ${
                                    row.status === "completed"
                                      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                      : "bg-amber-100 text-amber-700 border-amber-200"
                                  }`}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className="inline-block bg-violet-50 text-violet-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                  +{row.reward_points} PTS
                                </span>
                              </td>
                              <td className="px-5 py-3.5">
                                <TimeCell value={row.visited_at} />
                              </td>
                              <td className="px-5 py-3.5">
                                <TimeCell
                                  value={row.completed_at}
                                  empty={row.status === "started" ? "Not finished" : "—"}
                                />
                              </td>
                              <td className="px-5 py-3.5 text-gray-700 font-medium tabular-nums whitespace-nowrap">
                                {formatDuration(row.duration_seconds)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {compLastPage > 1 && (
                    <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Showing {completions.length} of {compTotal} completions
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={compPage <= 1 || loadingCompletions}
                          onClick={() => setCompPage((p) => Math.max(1, p - 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Previous
                        </button>
                        <span className="px-2 font-medium">
                          Page {compPage} of {compLastPage}
                        </span>
                        <button
                          disabled={compPage >= compLastPage || loadingCompletions}
                          onClick={() => setCompPage((p) => Math.min(compLastPage, p + 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <CopyToEventModal
        mode="move"
        isOpen={moveOpen}
        tasks={tasks.filter((task) => selectedIds.includes(task.id))}
        onClose={() => setMoveOpen(false)}
        onCopied={() => {
          setSelectedIds([]);
          loadTasks();
        }}
      />

      <CopyToEventModal
        isOpen={copyOpen}
        tasks={tasks.filter((task) => selectedIds.includes(task.id))}
        onClose={() => setCopyOpen(false)}
        onCopied={() => {
          setSelectedIds([]);
          loadTasks();
        }}
      />

      <PinTaskModal
        isOpen={!!pinTask}
        fixedTask={pinTask}
        onClose={() => setPinTask(null)}
        onPinned={() => setPoolReloadKey((k) => k + 1)}
      />

      <TaskModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveTask} editingTask={editingTask} />
      <TaskHistoryModal isOpen={Boolean(historyTask)} onClose={() => setHistoryTask(null)} task={historyTask} />
      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        subMessage={confirmModal.subMessage}
        confirmText={confirmModal.confirmText}
        confirmColor={confirmModal.confirmColor}
        icon={confirmModal.icon}
        loading={confirmModal.loading}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.action}
      />
    </div>
  );
};

export default TasksList;
